/**
 * The subtitles as the burner draws them: a parsed `SubtitleDocument` flattened
 * into plain, structured-clone-safe records that cross into the encoding worker
 * once and are read on every frame.
 *
 * TWO KINDS OF TRACK, and the page treats them differently on purpose:
 *
 *   styled  ASS/SSA. The file's own styles are used, scaled from its PlayRes to
 *           the frame: font size, primary/outline/shadow colours, outline and
 *           shadow widths, bold, italic, underline, alignment (numpad), margins,
 *           \pos, \move (linear, static between its times) and simple \fad. The
 *           page's size/colour/position controls do not apply.
 *   plain   Every other format. The page's controls decide the look: font size
 *           as a share of the frame height, text colour, outline colour and
 *           width, and position (bottom, middle, top). Inline <b>, <i>, <u> and
 *           <font color> from the file still apply per run, and a cue that names
 *           its own alignment ({\an8} in an SRT, a WebVTT `line:`) keeps it.
 *
 * NOT DRAWN, and said so rather than approximated: \t animations, karaoke
 * (\k fills), rotation (\frx/\fry/\frz and style Angle), shear, \clip/\iclip,
 * blur and \be, ScaleX/ScaleY and Spacing, vector drawings (\p), the seven-
 * argument \fade, and WebVTT regions. The text of such a cue is still drawn,
 * with its static styling.
 *
 * JASSUB (libass in WebAssembly) is NOT used. Its licence carries GPL/LGPL
 * parts (libass's dependencies), which this project does not ship; this canvas
 * renderer is ours and covers the common cases above.
 */

import type {
  Alignment,
  Color,
  Cue,
  InlineStyle,
  Point,
  Style,
  SubtitleDocument,
} from '../../../../../contracts/cue-model.ts';

const TICKS_PER_SECOND = 120_000;

export interface Rgba {
  readonly r: number;
  readonly g: number;
  readonly b: number;
  /** 0–1, 1 = opaque (the cue model's convention, not ASS's). */
  readonly a: number;
}

/** One run's look. Sizes are in SCRIPT pixels: the track's playRes, not the frame. */
export interface RunStyle {
  readonly fontName: string;
  readonly fontSize: number;
  readonly bold: boolean;
  readonly italic: boolean;
  readonly underline: boolean;
  readonly strikeOut: boolean;
  readonly fill: Rgba;
  readonly outline: Rgba;
  readonly outlineWidth: number;
  readonly shadow: Rgba;
  readonly shadowDepth: number;
}

export interface RenderRun {
  readonly text: string;
  readonly style: RunStyle;
}

export interface RenderLine {
  readonly runs: readonly RenderRun[];
}

export interface RenderMove {
  readonly from: Point;
  readonly to: Point;
  /** Seconds from the cue's start. */
  readonly t1: number;
  readonly t2: number;
}

export interface RenderCue {
  /** Seconds. A cue shows while start <= t < end. */
  readonly start: number;
  readonly end: number;
  readonly layer: number;
  /** File order, the tie-break after layer. */
  readonly order: number;
  readonly lines: readonly RenderLine[];
  readonly alignment: Alignment;
  /** Script pixels. */
  readonly margins: { readonly left: number; readonly right: number; readonly vertical: number };
  /** \pos, normalised 0–1 of the frame. */
  readonly point: Point | null;
  readonly move: RenderMove | null;
  /** Seconds of fade in and out. */
  readonly fade: { readonly in: number; readonly out: number } | null;
  /** BorderStyle 3: an opaque box in this colour behind each line instead of an outline. */
  readonly box: Rgba | null;
}

export interface RenderTrack {
  readonly styled: boolean;
  readonly playRes: { readonly x: number; readonly y: number };
  /** ScaledBorderAndShadow: outline and shadow widths scale with the frame like the font. */
  readonly scaleBorder: boolean;
  readonly wrap: boolean;
  readonly cues: readonly RenderCue[];
}

/** The page's controls, which apply to plain tracks only. */
export interface PlainOptions {
  /** Font size as a percentage of the frame height. */
  readonly sizePct: number;
  /** '#rrggbb' */
  readonly color: string;
  readonly outlineColor: string;
  readonly outline: 'none' | 'thin' | 'thick';
  readonly position: 'bottom' | 'middle' | 'top';
}

export const DEFAULT_PLAIN: PlainOptions = {
  sizePct: 5,
  color: '#ffffff',
  outlineColor: '#000000',
  outline: 'thin',
  position: 'bottom',
};

/**
 * A plain track's virtual script space. 1000×1000, so a font size of 50 is 5%
 * of the frame height and a 40px margin is 4% of the width: the same
 * non-uniform scaling libass applies to a PlayRes whose shape differs from the
 * frame's (font by height, x by width).
 */
export const PLAIN_RES = { x: 1000, y: 1000 } as const;

/** Outline width as a share of the font size, per control setting. */
export const OUTLINE_SHARE: Readonly<Record<PlainOptions['outline'], number>> = {
  none: 0,
  thin: 0.07,
  thick: 0.14,
};

export const POSITION_ALIGNMENT: Readonly<Record<PlainOptions['position'], Alignment>> = {
  bottom: 2,
  middle: 5,
  top: 8,
};

const PLAIN_MARGINS = { left: 40, right: 40, vertical: 50 } as const;
const PLAIN_FONT = 'Arial';

const TRANSPARENT: Rgba = { r: 0, g: 0, b: 0, a: 0 };

/** '#rgb' or '#rrggbb' to an opaque colour; anything else is white. */
export function hexToRgba(hex: string): Rgba {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m?.[1]) return { r: 255, g: 255, b: 255, a: 1 };
  const h = m[1].length === 3 ? [...m[1]].map((c) => c + c).join('') : m[1];
  return {
    r: Number.parseInt(h.slice(0, 2), 16),
    g: Number.parseInt(h.slice(2, 4), 16),
    b: Number.parseInt(h.slice(4, 6), 16),
    a: 1,
  };
}

const rgba = (c: Color): Rgba => ({ r: c.r, g: c.g, b: c.b, a: c.a });

/** The referenced style, or the table's default when the reference dangles. */
function styleOf(doc: SubtitleDocument, cue: Cue): Style | null {
  return doc.styles.byId[cue.styleId] ?? doc.styles.byId[doc.styles.defaultId] ?? null;
}

/** A style's own look, before any inline override. */
export function baseRunStyle(style: Style): RunStyle {
  return {
    fontName: style.fontName || PLAIN_FONT,
    fontSize: style.fontSize,
    bold: style.bold,
    italic: style.italic,
    underline: style.underline,
    strikeOut: style.strikeOut,
    fill: rgba(style.primaryColor),
    outline: rgba(style.outlineColor),
    outlineWidth: style.outlineWidth,
    shadow: rgba(style.shadowColor),
    shadowDepth: style.shadowDepth,
  };
}

/**
 * A run's resolved look: the inline delta over the base. Deltas are resolved
 * per run by the parser (cue-model §5), so run N never depends on run N-1.
 */
export function applyInline(base: RunStyle, inline: InlineStyle): RunStyle {
  const bold = inline.bold ?? (inline.weight === undefined ? base.bold : inline.weight >= 600);
  return {
    fontName: inline.fontName || base.fontName,
    fontSize: inline.fontSize ?? base.fontSize,
    bold,
    italic: inline.italic ?? base.italic,
    underline: inline.underline ?? base.underline,
    strikeOut: inline.strikeOut ?? base.strikeOut,
    fill: inline.primaryColor ? rgba(inline.primaryColor) : base.fill,
    outline: inline.outlineColor ? rgba(inline.outlineColor) : base.outline,
    outlineWidth: inline.outlineWidth ?? base.outlineWidth,
    shadow: inline.shadowColor ? rgba(inline.shadowColor) : base.shadow,
    shadowDepth: inline.shadowDepth ?? base.shadowDepth,
  };
}

/** The plain-track look from the page's controls. */
export function plainRunStyle(opts: PlainOptions): RunStyle {
  const fontSize = (opts.sizePct / 100) * PLAIN_RES.y;
  return {
    fontName: PLAIN_FONT,
    fontSize,
    bold: false,
    italic: false,
    underline: false,
    strikeOut: false,
    fill: hexToRgba(opts.color),
    outline: hexToRgba(opts.outlineColor),
    outlineWidth: fontSize * OUTLINE_SHARE[opts.outline],
    shadow: TRANSPARENT,
    shadowDepth: 0,
  };
}

/** Plain runs keep the file's emphasis and colour but never its sizes or outline. */
function plainInline(base: RunStyle, inline: InlineStyle): RunStyle {
  const bold = inline.bold ?? (inline.weight === undefined ? base.bold : inline.weight >= 600);
  return {
    ...base,
    bold,
    italic: inline.italic ?? base.italic,
    underline: inline.underline ?? base.underline,
    strikeOut: inline.strikeOut ?? base.strikeOut,
    fill: inline.primaryColor ? rgba(inline.primaryColor) : base.fill,
  };
}

function linesOf(cue: Cue, style: (inline: InlineStyle) => RunStyle): RenderLine[] {
  return cue.lines.map((line) => ({
    runs: line.runs
      .filter((run) => run.kind === 'text' && run.text !== '')
      .map((run) => ({ text: run.text, style: style(run.style) })),
  }));
}

function moveOf(cue: Cue, duration: number): RenderMove | null {
  const move = cue.position?.move;
  if (!move) return null;
  const t1 = move.startOffset === null ? 0 : move.startOffset / TICKS_PER_SECOND;
  const t2 = move.endOffset === null ? duration : move.endOffset / TICKS_PER_SECOND;
  // \move(x1,y1,x2,y2,0,0) and a reversed pair both mean "over the whole cue".
  return t2 > t1
    ? { from: move.from, to: move.to, t1, t2 }
    : { from: move.from, to: move.to, t1: 0, t2: duration };
}

function fadeOf(cue: Cue): RenderCue['fade'] {
  const simple = cue.fade?.simple;
  if (!simple) return null;
  return { in: simple.inMs / TICKS_PER_SECOND, out: simple.outMs / TICKS_PER_SECOND };
}

function isDrawn(cue: Cue): boolean {
  return !cue.commented && cue.kind !== 'comment';
}

export function isStyledFormat(doc: SubtitleDocument): boolean {
  return doc.meta.sourceFormat === 'ass' || doc.meta.sourceFormat === 'ssa';
}

/**
 * The track for a document. `opts` is read only for a plain track, so a page
 * can rebuild on every control change without caring which kind it has.
 */
export function buildTrack(doc: SubtitleDocument, opts: PlainOptions = DEFAULT_PLAIN): RenderTrack {
  const styled = isStyledFormat(doc);
  const plainBase = plainRunStyle(opts);
  const cues: RenderCue[] = [];
  let order = 0;
  for (const cue of doc.cues) {
    if (!isDrawn(cue)) continue;
    const start = cue.time.start / TICKS_PER_SECOND;
    const end = cue.time.end / TICKS_PER_SECOND;
    if (!(end > start)) continue;
    const style = styleOf(doc, cue);
    let lines: RenderLine[];
    let alignment: Alignment;
    let margins: RenderCue['margins'];
    let box: Rgba | null = null;
    if (styled && style) {
      const base = baseRunStyle(style);
      lines = linesOf(cue, (inline) => applyInline(base, inline));
      alignment = cue.position?.alignment ?? style.alignment;
      // ASS: a cue margin of 0 means "the style's".
      margins = {
        left: cue.margins?.left || style.margins.left,
        right: cue.margins?.right || style.margins.right,
        vertical: cue.margins?.vertical || style.margins.vertical,
      };
      if (style.borderStyle === 'opaque-box') box = rgba(style.outlineColor);
    } else {
      lines = linesOf(cue, (inline) => plainInline(plainBase, inline));
      alignment = cue.position?.alignment ?? POSITION_ALIGNMENT[opts.position];
      margins = PLAIN_MARGINS;
    }
    if (!lines.some((l) => l.runs.length > 0)) continue;
    cues.push({
      start,
      end,
      layer: cue.layer,
      order: order++,
      lines,
      alignment,
      margins,
      point: cue.position?.point ?? null,
      move: moveOf(cue, end - start),
      fade: fadeOf(cue),
      box,
    });
  }
  const res = doc.meta.resolution;
  return styled
    ? {
        styled: true,
        playRes: { x: res.x > 0 ? res.x : 384, y: res.y > 0 ? res.y : 288 },
        scaleBorder: doc.meta.scaledBorderAndShadow,
        wrap: doc.meta.wrapStyle !== 'none',
        cues,
      }
    : { styled: false, playRes: PLAIN_RES, scaleBorder: true, wrap: true, cues };
}

/*───────────────────────────────────────────────────────────────────────────────
  Which cues show at a time
───────────────────────────────────────────────────────────────────────────────*/

export interface CueIndex {
  /** Sorted by start, then file order. */
  readonly cues: readonly RenderCue[];
  /** maxEnd[i] = the latest end among cues[0..i], so a backward walk can stop early. */
  readonly maxEnd: Float64Array;
}

export function indexCues(cues: readonly RenderCue[]): CueIndex {
  const sorted = [...cues].sort((a, b) => a.start - b.start || a.order - b.order);
  const maxEnd = new Float64Array(sorted.length);
  let running = Number.NEGATIVE_INFINITY;
  sorted.forEach((cue, i) => {
    running = Math.max(running, cue.end);
    maxEnd[i] = running;
  });
  return { cues: sorted, maxEnd };
}

/**
 * Every cue showing at `t` (start <= t < end), in draw order: layer, then file
 * order. O(log n + k) for the common case: a binary search for the last cue
 * that has started, then a walk back that stops once no earlier cue can still
 * be showing.
 */
export function activeCues(index: CueIndex, t: number): RenderCue[] {
  const { cues, maxEnd } = index;
  let lo = 0;
  let hi = cues.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if ((cues[mid] as RenderCue).start <= t) lo = mid + 1;
    else hi = mid;
  }
  const out: RenderCue[] = [];
  for (let j = lo - 1; j >= 0 && (maxEnd[j] as number) > t; j--) {
    const cue = cues[j] as RenderCue;
    if (cue.end > t) out.push(cue);
  }
  return out.sort((a, b) => a.layer - b.layer || a.order - b.order);
}
